import React, { Fragment, useEffect, useMemo, useState } from "react";
import "./DeviceStatus.css";
import { format } from "date-fns";
import { Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AppIconButton from "../../Common/Button/IconButton";
import ConfiguredDataTable from "../../Common/DataTable/ConfiguredDataTable";
import { commonHeaders, deviceStatusTableConfig } from "./DeviceStatusConstants";
import {
    getAllowedStatusColumnKeysFromWebStatus,
    getDefaultColumnSequenceForDevice,
    getDefaultVisibleColumnsForDevice,
    getDeviceStatusStorageKey,
    isStatusColumnKeyAllowed,
    removeLegacyGlobalDeviceStatusColumnKey,
} from "./deviceStatusColumnUtils";
import { formatStatusPayloadValue, formatTableHeader } from "../../../utils/general";

const DisplayDeviceStatusData = ({
    deviceCategory,
    deviceId,
    deviceUsage,
    deviceStatusData = [],
    setDialog,
    setDialogContents,
    onVisibleColumnsChange,
    onVisibleColumnLabelsChange,
    onVisibleColumnDefsChange,
    pageIndex,
    pageSize,
    onFilteredRowCountChange,
}) => {
    const [headers, setHeaders] = useState({});

    useEffect(() => {
        removeLegacyGlobalDeviceStatusColumnKey();
    }, []);

    const allowedKeysFromWebStatus = useMemo(
        () => getAllowedStatusColumnKeysFromWebStatus(deviceUsage),
        [deviceUsage]
    );

    const storageKey = useMemo(() => getDeviceStatusStorageKey(deviceId), [deviceId]);

    const normalizeKey = (key) => {
        if (key === "ts") return "createdAt";
        return key;
    };

    const getPayloadEntry = (row, key) => {
        if (key === "createdAt") {
            return row?.payload?.createdAt ?? row?.payload?.ts;
        }
        return row?.payload?.[key];
    };

    const getDisplayValue = (row, key) => {
        if (key === "serialNumber") return String(row?.serialNumber ?? "");
        if (key === "createdAt") {
            const payloadValue = getPayloadEntry(row, key)?.value;
            if (String(payloadValue || "").length === 0) return "";
            return format(new Date(Number(payloadValue) * 1000), "dd MMM yyyy, hh:mm a");
        }
        const payloadEntry = getPayloadEntry(row, key);
        if (!payloadEntry) return "-";
        // Arrays/objects (e.g. switches `states`: [{ rmcn, rmcps }, ...]) — not raw .join (avoids [object Object]).
        return formatStatusPayloadValue(payloadEntry?.value);
    };

    const getSortValue = (row, key) => {
        if (key === "createdAt") {
            return Number(getPayloadEntry(row, key)?.value || 0);
        }
        return String(getDisplayValue(row, key) || "").toLowerCase();
    };

    useEffect(() => {
        const dataHeaders = {};

        if (allowedKeysFromWebStatus?.size) {
            allowedKeysFromWebStatus.forEach((key) => {
                if (key === "actions") return;
                dataHeaders[key] = {
                    header: formatTableHeader(key),
                    desc: "",
                };
            });
        }

        deviceStatusData?.forEach(d => {
            const payload = d?.payload ?? {};
            Object.keys(payload).forEach(h => {
                const normalizedKey = normalizeKey(h);
                if (!isStatusColumnKeyAllowed(normalizedKey, allowedKeysFromWebStatus)) {
                    return;
                }
                if (!dataHeaders?.hasOwnProperty(normalizedKey)) {
                    dataHeaders[normalizedKey] = {
                        header: formatTableHeader(payload[h]?.header !== "" ? payload[h]?.header : normalizedKey),
                        desc: payload[h]?.desc
                    };
                } else if (payload[h]?.header) {
                    dataHeaders[normalizedKey] = {
                        ...dataHeaders[normalizedKey],
                        header: formatTableHeader(payload[h]?.header !== "" ? payload[h]?.header : normalizedKey),
                        desc: payload[h]?.desc ?? dataHeaders[normalizedKey]?.desc,
                    };
                }
            });
        });
        delete dataHeaders?.mo;
        delete dataHeaders?.ty;

        const mappedCommonHeaders = Object.fromEntries(
            Object.entries(commonHeaders)
                .map(([key, val]) => [normalizeKey(key), val])
                .filter(([key]) => isStatusColumnKeyAllowed(key, allowedKeysFromWebStatus))
        );

        setHeaders({
            ...mappedCommonHeaders,
            ...dataHeaders,
            actions: { header: "Actions", desc: "" }
        });
    }, [allowedKeysFromWebStatus, deviceStatusData]);

    const statusRows = Array.isArray(deviceStatusData)
        ? deviceStatusData.map((element, index) => ({
            rowId: `${element?.payload?.ts?.value || "status"}-${index}`,
            raw: element,
            payload: element?.payload || {},
            serialNumber: index + 1,
        }))
        : [];

    // Per deviceId: localStorage key + defaults derived from this device's web/status keys.
    const tableConfig = useMemo(() => {
        const defaultVisibleColumns = getDefaultVisibleColumnsForDevice(allowedKeysFromWebStatus);
        const columnSequence = getDefaultColumnSequenceForDevice(allowedKeysFromWebStatus);
        const { actmp, acmd, ...sharedColumnMeta } = deviceStatusTableConfig.columns;

        return {
            ...deviceStatusTableConfig,
            sessionStorageKey: storageKey,
            defaultVisibleColumns,
            columnSequence,
            columns: {
                ...sharedColumnMeta,
                ...(defaultVisibleColumns.includes("actmp") ? { actmp } : {}),
                ...(defaultVisibleColumns.includes("acmd") ? { acmd } : {}),
                actions: {
                    ...deviceStatusTableConfig.columns.actions,
                    headerCellSx: {
                        ...deviceStatusTableConfig.columns.actions?.headerCellSx,
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    },
                },
            },
        };
    }, [allowedKeysFromWebStatus, storageKey]);

    const normalizedHeaders = useMemo(() => (
        Object.fromEntries(
            Object.entries(headers).map(([key, val]) => [normalizeKey(key), { ...val, header: val?.header || formatTableHeader(key) }])
        )
    ), [headers]);

    return (
        <Fragment>
            {deviceCategory && deviceCategory !== "" && deviceStatusData && (
                <ConfiguredDataTable
                    key={storageKey}
                    config={tableConfig}
                    headers={normalizedHeaders}
                    rows={statusRows}
                    getCellDisplayValue={getDisplayValue}
                    getCellSortValue={getSortValue}
                    onVisibleColumnsChange={onVisibleColumnsChange}
                    onVisibleColumnLabelsChange={onVisibleColumnLabelsChange}
                    onVisibleColumnDefsChange={onVisibleColumnDefsChange}
                    onFilteredRowCountChange={onFilteredRowCountChange}
                    page={pageIndex}
                    pageSize={pageSize}
                    renderCell={({ column, row }) => {
                        if (column.key === "actions") {
                            return (
                                <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                    <AppIconButton
                                        variant="primary"
                                        size="small"
                                        icon={<VisibilityIcon fontSize="small" />}
                                        tooltip="View status details"
                                        onClick={() => {
                                            setDialogContents({ title: "Status Details", data: row.raw });
                                            setDialog(true);
                                        }}
                                    />
                                </Box>
                            );
                        }
                        const displayValue = getDisplayValue(row, column.key);
                        return (
                            <Box className={String(displayValue || "").length > 200 ? "breakWord" : ""}>
                                {displayValue}
                            </Box>
                        );
                    }}
                    dataTableProps={{
                        rowKey: "rowId",
                        minWidth: "100%",
                        maxHeight: 400,
                        striped: false,
                        hoverEffect: false,
                        tableContainerSx: { boxShadow: "none", borderRadius: 0, width: "100%", minWidth: 0 },
                        sx: { mb: 2 },
                        msgForDisplay: "No Data found",
                    }}
                />
            )}
        </Fragment>
    );
};

export default DisplayDeviceStatusData